(() => {
  class ProductMediaGalleryCustom extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.zoomButtons = [];
      this.progressIndicator = null;
      this.mobileProgressIndicator = null;
      this.mobileProgressDots = [];
      this.previousButton = null;
      this.nextButton = null;
      this.scroller = null;
      this.activeIndex = 0;
      this.ticking = false;
      this.unsubscribeVariantChange = null;
      this.mobileQuery = window.matchMedia('(max-width: 989px)');

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPreviousClick = this.onPreviousClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onMobileDotClick = this.onMobileDotClick.bind(this);
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-pmg-media-id]'));
      this.zoomButtons = Array.from(this.querySelectorAll('.pmg-custom__zoom-button'));
      this.progressIndicator = this.querySelector('[data-pmg-progress]');
      this.mobileProgressIndicator = this.querySelector('[data-pmg-mobile-progress]');
      this.mobileProgressDots = Array.from(this.querySelectorAll('.pmg-custom__mobile-progress-dot'));
      this.previousButton = this.querySelector('[data-pmg-prev]');
      this.nextButton = this.querySelector('[data-pmg-next]');
      this.scroller = this.getScroller();

      if (!this.items.length) return;

      this.bindZoom();
      this.previousButton?.addEventListener('click', this.onPreviousClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.mobileProgressDots.forEach((dot) => dot.addEventListener('click', this.onMobileDotClick));
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('wheel', this.onWheel);
      window.addEventListener('resize', this.onResize);

      if (window.PUB_SUB_EVENTS?.variantChange && typeof window.subscribe === 'function') {
        this.unsubscribeVariantChange = window.subscribe(window.PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      } else if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      this.updateProgress();
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.onPreviousClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.mobileProgressDots.forEach((dot) => dot.removeEventListener('click', this.onMobileDotClick));
      this.scroller?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('resize', this.onResize);
      this.unsubscribeVariantChange?.();
    }

    bindZoom() {
      this.zoomButtons.forEach((button) => {
        const image = button.querySelector('.pmg-custom__image');
        if (!image) return;

        button.addEventListener('click', (event) => {
          if (this.isMobile()) return;

          event.preventDefault();
          this.moveZoomOrigin(button, event);
          button.classList.toggle('is-zoomed');
        });

        button.addEventListener('mousemove', (event) => {
          if (!button.classList.contains('is-zoomed')) return;
          this.moveZoomOrigin(button, event);
        });

        button.addEventListener('mouseleave', () => {
          if (!button.classList.contains('is-zoomed')) {
            image.style.transformOrigin = '';
          }
        });
      });
    }

    moveZoomOrigin(button, event) {
      const image = button.querySelector('.pmg-custom__image');
      if (!image) return;

      const rect = button.getBoundingClientRect();
      const x = Math.min(Math.max(((event.clientX - rect.left) / rect.width) * 100, 0), 100);
      const y = Math.min(Math.max(((event.clientY - rect.top) / rect.height) * 100, 0), 100);
      image.style.transformOrigin = `${x}% ${y}%`;
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.updateProgress();
        this.ticking = false;
      });
    }

    onResize() {
      const nextScroller = this.getScroller();
      if (nextScroller !== this.scroller) {
        this.scroller?.removeEventListener('scroll', this.onScroll);
        this.scroller = nextScroller;
        this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      }
      this.updateProgress();
    }

    onWheel(event) {
      if (this.isMobile()) return;

      const atBottom = this.scroller.scrollTop + this.scroller.clientHeight >= this.scroller.scrollHeight - 2;
      const atTop = this.scroller.scrollTop <= 0;

      if (event.deltaY > 0 && atBottom) {
        return;
      }

      if (event.deltaY < 0 && atTop) {
        return;
      }

      event.stopPropagation();
    }

    onPreviousClick() {
      this.scroller.scrollBy({ top: -this.getScrollStep(), behavior: 'smooth' });
    }

    onNextClick() {
      this.scroller.scrollBy({ top: this.getScrollStep(), behavior: 'smooth' });
    }

    onMobileDotClick(event) {
      if (!this.isMobile()) return;

      const index = Number(event.currentTarget.dataset.pmgIndex);
      if (Number.isNaN(index)) return;

      this.scrollToIndex(index);
    }

    onVariantChange(event) {
      const variant = event?.data?.variant;
      const featuredMediaId = variant?.featured_media?.id;
      if (!featuredMediaId) return;

      const index = this.items.findIndex((item) => item.dataset.pmgMediaId === String(featuredMediaId));
      if (index >= 0) this.scrollToIndex(index);
    }

    setActiveMedia(mediaId, prepend = false) {
      const item = this.items.find((galleryItem) => galleryItem.dataset.mediaId === mediaId);
      if (!item) return;

      if (prepend && item.parentElement?.firstElementChild !== item) {
        item.parentElement.prepend(item);
        this.items = Array.from(this.querySelectorAll('[data-pmg-media-id]'));
      }

      this.scrollToIndex(this.items.indexOf(item));
    }

    scrollToIndex(index) {
      const item = this.items[index];
      if (!item) return;

      if (this.isMobile()) {
        this.scroller.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
      } else {
        this.scroller.scrollTo({ top: item.offsetTop, behavior: 'smooth' });
      }
    }

    getScrollStep() {
      return Math.max(this.clientHeight * 0.72, 240);
    }

    updateProgress() {
      if (!this.items.length) return;

      const mobile = this.isMobile();
      const scrollRange = mobile
        ? Math.max(this.scroller.scrollWidth - this.scroller.clientWidth, 1)
        : Math.max(this.scroller.scrollHeight - this.scroller.clientHeight, 1);
      const currentScroll = mobile ? this.scroller.scrollLeft : this.scroller.scrollTop;
      const scrolled = Math.min(Math.max(currentScroll, 0), scrollRange);
      const progress = scrollRange <= 1 ? 1 : scrolled / scrollRange;

      this.activeIndex = this.getActiveIndex();
      this.updateScrollEdgeState();
      this.updateProgressIndicator(progress);
      this.updateMobileProgressIndicator();
      this.updateButtons();
      this.updateActiveItem();
    }

    updateScrollEdgeState() {
      if (this.isMobile()) {
        this.classList.remove('pmg-custom--at-scroll-edge', 'pmg-custom--at-bottom');
        return;
      }

      const atBottom = this.scroller.scrollTop + this.scroller.clientHeight >= this.scroller.scrollHeight - 2;
      const atTop = this.scroller.scrollTop <= 0;
      this.classList.toggle('pmg-custom--at-scroll-edge', atTop || atBottom);
      this.classList.toggle('pmg-custom--at-bottom', atBottom);
    }

    updateProgressIndicator(progress) {
      if (!this.progressIndicator || this.isMobile()) return;

      const track = this.progressIndicator.parentElement;
      const trackHeight = track?.clientHeight || 0;
      const indicatorHeight = this.progressIndicator.clientHeight || 0;
      const travel = Math.max(trackHeight - indicatorHeight - 32, 0);
      this.progressIndicator.style.transform = `translate3d(-50%, ${progress * travel}px, 0)`;
    }

    updateMobileProgressIndicator() {
      if (!this.mobileProgressDots.length || !this.isMobile()) return;

      const minWidth = 4;
      const maxWidth = 20;

      this.mobileProgressDots.forEach((dot, index) => {
        const isActive = index === this.activeIndex;

        dot.style.width = `${isActive ? maxWidth : minWidth}px`;
        dot.style.background = isActive ? '#252525' : '#d1d1d1';
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    updateButtons() {
      if (this.isMobile()) return;

      const atBottom = this.scroller.scrollTop + this.scroller.clientHeight >= this.scroller.scrollHeight - 2;
      const atTop = this.scroller.scrollTop <= 0;

      if (this.previousButton) this.previousButton.disabled = atTop;
      if (this.nextButton) this.nextButton.disabled = atBottom;
    }

    updateActiveItem() {
      this.items.forEach((item, index) => {
        item.classList.toggle('is-active', index === this.activeIndex);
      });
    }

    getActiveIndex() {
      const mobile = this.isMobile();
      const viewportMarker = mobile
        ? this.scroller.scrollLeft + this.scroller.clientWidth * 0.5
        : this.scroller.scrollTop + this.scroller.clientHeight * 0.45;
      let activeIndex = 0;

      this.items.forEach((item, index) => {
        const itemStart = mobile ? item.offsetLeft : item.offsetTop;
        if (itemStart <= viewportMarker) {
          activeIndex = index;
        }
      });

      return activeIndex;
    }

    isMobile() {
      return this.mobileQuery.matches;
    }

    getScroller() {
      return this.isMobile() ? this.querySelector('.pmg-custom__grid') || this : this;
    }
  }

  if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', ProductMediaGalleryCustom);
  }
})();
